import { createDriveItem, DriveItem } from '@/drive/entities/DriveItem/DriveItem';
import { AccessInheritanceResponse } from '@/drive/types/AccessInheritanceResponse';

export class DriveItemsResponse<T = DriveItem> {
  private responses: (T | { error: string })[];

  constructor(
    content: unknown,
    private accessChanges: AccessInheritanceResponse[] = [],
  ) {
    this.responses = content as (T | { error: string })[];
  }

  static fromDriveItems<T extends DriveItem = DriveItem>(
    content: unknown,
    createItem: (item: Any) => T = createDriveItem as (item: Any) => T,
  ): DriveItemsResponse<T> {
    const arr = (content as Any[]) ?? [];

    return new DriveItemsResponse<T>(
      arr.map((item) => {
        if (DriveItemsResponse.itemHasError(item)) {
          return item;
        }

        return createItem(item);
      }),
      arr.flatMap((item) => (item?.accessChanges as Nullable<AccessInheritanceResponse[]>) ?? []),
    );
  }

  getAll(): (T | { error: string })[] {
    return this.responses;
  }

  getAccessChanges(): AccessInheritanceResponse[] {
    return this.accessChanges;
  }

  getErrors(): string[] {
    return this.responses
      .filter((item): item is { error: string } => DriveItemsResponse.itemHasError(item))
      .map((item) => item.error);
  }

  getItems(): T[] {
    return this.responses.filter((item): item is T => !DriveItemsResponse.itemHasError(item));
  }

  getItemsByRequestIndex(): Nullable<T>[] {
    return this.responses.map((item) => (DriveItemsResponse.itemHasError(item) ? null : (item as T)));
  }

  hasErrors(): boolean {
    return this.responses.some((item) => DriveItemsResponse.itemHasError(item));
  }

  private static itemHasError(item: Any): boolean {
    return 'error' in item && typeof item.error === 'string';
  }
}
